import { Feature } from "@omni/types/api/product/bff/shared/feature.model";

import {
    ConditionParams,
    ProductConfiguration
} from "./productConfiguration.types";
import LicensePlate from "./visuals/licensePlate/licensePlate.component";
import {
    LicensePlateConfiguration,
    LicensePlateSubmitModel
} from "./visuals/licensePlate/licensePlate.types";
import { PaintConfigurator } from "./visuals/paintConfiguration/paintConfigurator.component";
import {
    PaintConfiguration,
    PaintConfigurationSubmitModel
} from "./visuals/paintConfiguration/paintConfigurator.types";
import { YieldCalculator } from "./visuals/yieldCalculator/yieldCalculator.component";
import {
    YIELD_CONTENT_FEATURE_CODE,
    YIELD_PER_LITER_FEATURE_CODE,
    YIELD_PER_PACKAGE_FEATURE_CODE
} from "./visuals/yieldCalculator/yieldCalculator.helpers";
import {
    YieldCalculatorSubmitModel,
    YieldConfiguration
} from "./visuals/yieldCalculator/yieldCalculator.types";

const licensePlateProductSKUs = [
    "341819",
    "487684",
    "509641",
    "892028",
    "1054490",
    "1054491",
    "1054492",
    "1054493"
];

const paintProductSKUs = [
    "141950",
    "141947",
    "141949",
    "141946",
    "141948",
    "141945",
    "141944",
    "104302",
    "104304",
    "104299",
    "104301",
    "104296",
    "104298",
    "917745",
    "184236",
    "184238",
    "917721",
    "917723"
];

const yieldCalculatorIdentifiers = [
    "muur-en-plafondverf",
    "primers",
    "lakken",
    "vloerverf",
    "verf---houtbescherming"
];

const hasYieldFeature = (features: Array<Feature>) => {
    const hasYieldPerPackage = features.some(
        ({ code }) => code === YIELD_PER_PACKAGE_FEATURE_CODE
    );
    const hasYieldContent = features.some(
        ({ code }) => code === YIELD_CONTENT_FEATURE_CODE
    );
    const hasYieldPerLiter = features.some(
        ({ code }) => code === YIELD_PER_LITER_FEATURE_CODE
    );
    return hasYieldPerPackage || (hasYieldContent && hasYieldPerLiter);
};

export const productConfigurations: ProductConfiguration[] = [
    {
        condition: ({ sku }) => !!sku && licensePlateProductSKUs.includes(sku),
        visual: LicensePlate,
        productSubType: "licenseplates",
        configPaths: ["chosenLicensePlateType", "chosenLicensePlateText"],
        dataPaths: (config) => config,
        previewPath: "chosenLicensePlateText",
        withOrigin: true
    } satisfies ProductConfiguration<
        LicensePlateConfiguration,
        LicensePlateSubmitModel
    >,
    {
        condition: ({ sku }) => !!sku && paintProductSKUs.includes(sku),
        visual: PaintConfigurator,
        productSubType: "paints",
        configPaths: [
            "paintInfo.colorHex",
            "paintInfo.colorName",
            "paintInfo.colorId"
        ],
        dataPaths: (config) => ({
            chosenColorId: config.paintInfo.colorId
        }),
        previewPath: "paintInfo.colorName",
        withOrigin: false
    } satisfies ProductConfiguration<
        PaintConfiguration,
        PaintConfigurationSubmitModel
    >,
    {
        condition: ({ features = [], identifier = "" }) =>
            features.length > 0 &&
            !!identifier &&
            hasYieldFeature(features) &&
            yieldCalculatorIdentifiers.includes(identifier),
        visual: YieldCalculator,
        productSubType: "",
        configPaths: [],
        dataPaths: () => ({}),
        previewPath: undefined,
        withOrigin: false
    } satisfies ProductConfiguration<
        YieldConfiguration,
        YieldCalculatorSubmitModel
    >
];

export const getProductConfiguration = (
    params: ConditionParams
): ProductConfiguration | undefined => {
    return productConfigurations.find(({ condition }) => condition(params));
};

export const isConfigurableProduct = (params: ConditionParams): boolean =>
    productConfigurations.some(({ condition }) => condition(params));
